import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Select,
  Button,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faFileExport } from "@fortawesome/free-solid-svg-icons";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// สีตามประเภทปศุสัตว์
const typeColors = {
  POULTRY: "#ef7c8e",
  CATTLE: "#879e82",
  PIG: "#5e87f5",
};

// สีสำหรับการเบิก
const requisitionColors = {
  CATTLE: "#ff8fab",
  POULTRY: "#74a892",
  PIG: "#1572a1",
};
// ฟังก์ชันแปลงเลขเดือนเป็นชื่อเดือนภาษาไทย
const getThaiMonth = (monthNumber) => {
  const thaiMonths = [
    "มกราคม",
    "กุมภาพันธ์",
    "มีนาคม",
    "เมษายน",
    "พฤษภาคม",
    "มิถุนายน",
    "กรกฎาคม",
    "สิงหาคม",
    "กันยายน",
    "ตุลาคม",
    "พฤศจิกายน",
    "ธันวาคม",
  ];
  return thaiMonths[monthNumber - 1] || "";
};
const VaccineRecReq = () => {
  const { sessionId } = useAuth();
  const [receiptData, setReceiptData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [hoveredType, setHoveredType] = useState(null);
  const componentRef = useRef();
// ดึงเดือนปัจจุบัน
  const currentMonth = new Date().getMonth() + 1; // getMonth() คืนค่า 0-11, จึงบวก 1
  const thaiMonthName = getThaiMonth(currentMonth);
  // ดึงข้อมูลจาก API
  useEffect(() => {
    const fetchReceipts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/getVaccineReceiveRequisitionSummary",
          data: {
            Month: "2025-04",
          },
          sessionId: sessionId || "",
        });
        if (response.result === "success" && response.summary) {
          const groupedData = response.summary.reduce((acc, item) => {
            const key = `${item.LivestockType}_${item.VaccineNameTH}`;
            if (!acc[key]) {
              acc[key] = {
                LivestockType: item.LivestockType,
                VaccineNameTH: item.VaccineNameTH,
                VaccineNameEN: item.VaccineNameEN,
                TotalQuantityReceive: 0,
                TotalValueReceive: 0,
                TotalQuantityRequisition: 0,
                TotalValueRequisition: 0,
              };
            }
            acc[key].TotalQuantityReceive += Number(item.TotalQuantityReceive);
            acc[key].TotalValueReceive += Number(item.TotalValueReceive);
            acc[key].TotalQuantityRequisition += Number(
              item.TotalQuantityRequisition
            );
            acc[key].TotalValueRequisition += Number(
              item.TotalValueRequisition
            );
            return acc;
          }, {});

          const receipts = Object.values(groupedData);
          setReceiptData(receipts);

          const summary = receipts.reduce((acc, item) => {
            const type = item.LivestockType;
            if (!acc[type]) {
              acc[type] = {
                LivestockType: type,
                QuantityReceive: 0,
                ValueReceive: 0,
                QuantityRequisition: 0,
                ValueRequisition: 0,
              };
            }
            acc[type].QuantityReceive += item.TotalQuantityReceive;
            acc[type].ValueReceive += item.TotalValueReceive;
            acc[type].QuantityRequisition += item.TotalQuantityRequisition;
            acc[type].ValueRequisition += item.TotalValueRequisition;
            return acc;
          }, {});
          setSummaryData(Object.values(summary));
        } else {
          setReceiptData([]);
          setSummaryData([]);
          console.error("Failed to fetch receipts:", response.error);
        }
      } catch (error) {
        console.error("Error fetching receipts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) fetchReceipts();
  }, [sessionId]);

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    const activeType = hoveredType || selectedType;
    if (activeType) {
      return receiptData
        .filter((item) => item.LivestockType === activeType)
        .map((item) => ({
          name: item.VaccineNameTH,
          receive: item.TotalQuantityReceive,
          requisition: item.TotalQuantityRequisition,
          valueReceive: item.TotalValueReceive,
          valueRequisition: item.TotalValueRequisition,
          type: item.LivestockType,
        }));
    }
    return summaryData.map((item) => ({
      name:
        item.LivestockType === "POULTRY"
          ? "สัตว์ปีก"
          : item.LivestockType === "CATTLE"
          ? "โค-กระบือ"
          : "สุกร",
      receive: item.QuantityReceive,
      requisition: item.QuantityRequisition,
      valueReceive: item.ValueReceive,
      valueRequisition: item.ValueRequisition,
      type: item.LivestockType,
    }));
  };

  // คอลัมน์ตาราง
  const columns = [
    {
      name: "ประเภทปศุสัตว์",
      selector: (row) => row.LivestockType,
      sortable: true,
      cell: (row) => (
        <HStack spacing={2}>
          <Box
            w="8px"
            h="12px"
            bg={typeColors[row.LivestockType]}
            borderRadius="sm"
          />
          <Text>
            {row.LivestockType === "POULTRY"
              ? "สัตว์ปีก"
              : row.LivestockType === "CATTLE"
              ? "โค-กระบือ"
              : "สุกร"}
          </Text>
        </HStack>
      ),
    },
    {
      name: "จำนวนรับ (โดส)",
      selector: (row) => row.QuantityReceive,
      sortable: true,
      format: (row) => row.QuantityReceive.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่ารับ (บาท)",
      selector: (row) => row.ValueReceive,
      sortable: true,
      format: (row) => row.ValueReceive.toLocaleString("th-TH"),
    },
    {
      name: "จำนวนเบิก (โดส)",
      selector: (row) => row.QuantityRequisition,
      sortable: true,
      format: (row) => row.QuantityRequisition.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่าเบิก (บาท)",
      selector: (row) => row.ValueRequisition,
      sortable: true,
      format: (row) => row.ValueRequisition.toLocaleString("th-TH"),
    },
  ];

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <Text>
            <strong>ชื่อ:</strong> {label}
          </Text>
          <Text>
            <strong>จำนวนรับ:</strong> {data.receive.toLocaleString("th-TH")}{" "}
            โดส
          </Text>
          <Text>
            <strong>มูลค่ารับ:</strong>{" "}
            {data.valueReceive.toLocaleString("th-TH")} บาท
          </Text>
          <Text>
            <strong>จำนวนเบิก:</strong>{" "}
            {data.requisition.toLocaleString("th-TH")} โดส
          </Text>
          <Text>
            <strong>มูลค่าเบิก:</strong>{" "}
            {data.valueRequisition.toLocaleString("th-TH")} บาท
          </Text>
        </Box>
      );
    }
    return null;
  };

  // ฟังก์ชันสำหรับ export เป็น PDF
  const exportToPDF = () => {
    const input = componentRef.current;
    html2canvas(input, {
      scale: 1.5,
      useCORS: true,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.offsetWidth,
      windowHeight: document.documentElement.offsetHeight,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("vaccine_receive_requisition_report.pdf");
    });
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iBlue.500"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5} justifyContent="space-between">
          <HStack spacing={5}>
            <FontAwesomeIcon icon={faChartBar} color="white" size="xl" />
            <Heading size={{ base: "md", md: "md" }} fontWeight="normal">
              สรุปการรับและเบิกวัคซีนประจำเดือน {thaiMonthName}
            </Heading>
          </HStack>
          <Button
            className="export-button"
            leftIcon={<FontAwesomeIcon icon={faFileExport} />}
            colorScheme="iBlue"
            variant="solid"
            size="sm"
            mr={5}
            onClick={exportToPDF}
          >
            ส่งออกเป็น PDF
          </Button>
        </HStack>
      </Box>

      {/* เนื้อหาที่ต้องการ export */}
      <Box ref={componentRef}>
        {/* ตาราง */}
        {isLoading ? (
          <Text textAlign="center" color="gray.500" p={5}>
            กำลังโหลดข้อมูล...
          </Text>
        ) : summaryData.length > 0 ? (
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={summaryData}
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              onRowMouseEnter={(row) => setHoveredType(row.LivestockType)}
              onRowMouseLeave={() => setHoveredType(null)}
            />
          </Box>
        ) : (
          <Text textAlign="center" color="gray.500" p={5}>
            ไม่มีข้อมูลสำหรับแสดง
          </Text>
        )}

        {/* ตัวกรอง */}
        <FormControl maxW="300px" m={5}>
          <FormLabel fontSize="xl" color="gray">
            เลือกประเภทปศุสัตว์
          </FormLabel>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            placeholder="ทั้งหมด"
            colorScheme="iBlue"
          >
            <option value="POULTRY">สัตว์ปีก</option>
            <option value="CATTLE">โค-กระบือ</option>
            <option value="PIG">สุกร</option>
          </Select>
        </FormControl>

        {/* กราฟ */}
        {chartData().length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData()}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              background={{ fill: "#fff" }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={100}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{
                  value: "จำนวนวัคซีน (โดส)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -5,
                }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => value.toLocaleString("th-TH")}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 10 }}
              />
              <Bar dataKey="receive" name="จำนวนรับ" barSize={25} fill="#a0a0a0">
                {chartData().map((entry, index) => (
                  <Cell
                    key={`receive-${index}`}
                    fill={typeColors[entry.type] || "#16A085"}
                  />
                ))}
              </Bar>
              <Bar dataKey="requisition" name="จำนวนเบิก" barSize={25} fill="#808080">
                {chartData().map((entry, index) => (
                  <Cell
                    key={`requisition-${index}`}
                    fill={typeColors[entry.type]} opacity={0.4} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Text textAlign="center" color="gray.500" p={5}>
            ไม่มีข้อมูลสำหรับแสดงกราฟ
          </Text>
        )}
      </Box>

      {/* CSS สำหรับการ export */}
      <style>
        {`
          @media print {
            .rdt_Table {
              overflow: visible !important;
            }
            .rdt_TableRow {
              min-height: 40px !important;
              padding-top: 8px !important;
              padding-bottom: 8px !important;
            }
            .rdt_TableCell {
              line-height: 1.5 !important;
              display: flex !important;
              align-items: center !important;
              padding-top: 8px !important;
              padding-bottom: 8px !important;
            }
            .export-button {
              display: none !important;
            }
            .recharts-responsive-container {
              width: 100% !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default VaccineRecReq;